import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import moment from 'moment'
import SendIcon from '@mui/icons-material/Send'
import TuneIcon from '@mui/icons-material/Tune'
import { useCountUp } from '../../../userPages/dashboard/managerOverview/overviewTheme'

const GlassChip = ({ label, value, accent }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{
      px: 1.5,
      py: 0.75,
      borderRadius: 5,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.14)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: accent ?? '#4DBBC4',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.95rem' }}>
      {value}
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.8rem' }}>
      {label}
    </Typography>
  </Stack>
)

const plural = (count, word) => `${word}${count === 1 ? '' : 's'}`

const ATTENTION = '#F28B1F'
const CALM = '#4DBBC4'

// Dark briefing hero matching the workforce dashboard - headline compliance
// rate, the counts that need a person to act, and the two primary actions
const OHHero = ({ orgSettings, totals, onRequestOutstanding, requesting }) => {
  const rate = useCountUp(Math.round(totals?.complianceRate ?? 0))
  const hasStaff = (totals?.activeStaff ?? 0) > 0

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 5 },
        color: '#FFFFFF',
        background:
          'linear-gradient(120deg, #1A2230 0%, #242F40 55%, #1E3A45 100%)',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(600px 300px at 85% -20%, rgba(35, 154, 164, 0.35), transparent 70%),' +
            'radial-gradient(500px 260px at -10% 110%, rgba(204, 164, 59, 0.16), transparent 70%),' +
            'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: 'auto, auto, 22px 22px',
        }}
      />

      <Stack
        position="relative"
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={3}
      >
        <Stack gap={1}>
          <Typography
            sx={{
              color: CALM,
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {orgSettings?.orgName ?? 'Company'} · Occupational Health
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: { xs: '1.7rem', md: '2.2rem' },
              lineHeight: 1.15,
            }}
          >
            Fitness to work, at a glance
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            {moment().format('dddd D MMMM YYYY')} — screening coverage and
            flagged health declarations across your active staff.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
            <GlassChip
              label={`${plural(totals?.outstanding ?? 0, 'screening')} outstanding`}
              value={totals?.outstanding ?? 0}
              accent={(totals?.outstanding ?? 0) > 0 ? ATTENTION : CALM}
            />
            <GlassChip
              label={`${plural(totals?.flagged ?? 0, 'staff member')} with flags`}
              value={totals?.flagged ?? 0}
              accent={(totals?.flagged ?? 0) > 0 ? ATTENTION : CALM}
            />
            <GlassChip
              label={`${plural(totals?.requested ?? 0, 'request')} awaiting staff`}
              value={totals?.requested ?? 0}
            />
            <GlassChip
              label="awaiting appraisal refresh"
              value={totals?.awaitingAppraisalRefresh ?? 0}
            />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              disabled={
                requesting ||
                (totals?.outstanding ?? 0) - (totals?.requested ?? 0) <= 0
              }
              onClick={onRequestOutstanding}
              sx={{
                backgroundColor: '#FFFFFF',
                color: '#1A2230',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.88)' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {requesting ? 'Requesting…' : 'Request from everyone outstanding'}
            </Button>
            <Button
              component={Link}
              to="/site-configuration/occupational-health"
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              sx={{
                color: '#FFFFFF',
                borderColor: 'rgba(255,255,255,0.4)',
                '&:hover': {
                  borderColor: '#FFFFFF',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Configure screening
            </Button>
          </Stack>
        </Stack>

        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0}>
          <Typography
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '3.4rem', md: '4.4rem' },
              lineHeight: 1,
              color: '#FFFFFF',
            }}
          >
            {hasStaff ? `${rate}%` : '—'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            {hasStaff
              ? `${totals.complete} of ${totals.activeStaff} active staff screened`
              : 'no active staff yet'}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default OHHero
